import React, { useState } from 'react';
import {
  Container, Card, CardContent, Typography, Button, Box,
  CircularProgress, Alert, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, IconButton, Tabs, Tab
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { API_URL } from '../../config';

// Format date string without timezone conversion
const fmtDate = (val) => {
  if (!val) return '';
  const s = val.toString().substring(0, 10);
  const [yr, mo, dy] = s.split('-');
  return `${dy}/${mo}/${yr}`;
};

const fmt2 = (val, decimals = 3) =>
  val != null ? Number(val).toFixed(decimals) : '—';

// Shared header cell style
const hdrCell = {
  fontWeight: 700,
  textAlign: 'center',
  py: { xs: 1.5, md: 2 },
  px: { xs: 1, md: 1.5 },
  color: '#ffffff',
  fontSize: { xs: '0.72rem', md: '0.82rem' },
  letterSpacing: '0.4px',
  textTransform: 'uppercase',
  borderRight: '1px solid rgba(255,255,255,0.2)',
  whiteSpace: 'nowrap',
  background: 'linear-gradient(135deg, #ffb74d 0%, #ffa726 100%)',
};

const dataCell = (extra = {}) => ({
  py: { xs: 1, md: 1.2 },
  px: { xs: 1, md: 1.5 },
  fontSize: { xs: '0.75rem', md: '0.82rem' },
  borderRight: '1px solid #e0e0e0',
  whiteSpace: 'nowrap',
  ...extra,
});

const LoadingSaleView = ({ onBackClick }) => {
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate]     = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [data, setData]         = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 100;

  // ── Detail columns ──────────────────────────────────────────────────────────
  const detailHeaders = [
    { key: 'LOADINGDATE',              label: 'Loading Date',    fmt: fmtDate },
    { key: 'INVOICE_NUMBER',           label: 'Invoice No' },
    { key: 'INVOICE_DATE',             label: 'Invoice Date',    fmt: fmtDate },
    { key: 'CONTAINER_NO',             label: 'Container No' },
    { key: 'SUBORDERNO',               label: 'Sub Order No' },
    { key: 'ORDERNO',                  label: 'Order No' },
    { key: 'Client_Name',              label: 'Client' },
    { key: 'IST_NOTIFY_CONSIGNEE_NAME',label: 'Consignee' },
    { key: 'COUNTRY',                  label: 'Country' },
    { key: 'MARKET_NAME',              label: 'Market' },
    { key: 'MARKETGROUP',              label: 'Market Group' },
    { key: 'PALLET_NO',                label: 'Pallet No' },
    { key: 'GRADE',                    label: 'Grade' },
    { key: 'QUALITY',                  label: 'Quality' },
    { key: 'TYPE',                     label: 'Type' },
    { key: 'CERTIFICATES',             label: 'Certificates' },
    { key: 'IS_PRIMMED',               label: 'Primmed' },
    { key: 'UF_PF_PRESTIGIO',          label: 'UF/PF/Prestigio' },
    { key: 'LENGTH',                   label: 'Length' },
    { key: 'WIDTH',                    label: 'Width' },
    { key: 'THICKNESS',                label: 'Thickness' },
    { key: 'CUTLENGTH',                label: 'Cut Length' },
    { key: 'CUTWIDTH',                 label: 'Cut Width' },
    { key: 'PCS',                      label: 'PCS' },
    { key: 'CBM',                      label: 'CBM',             fmt: (v) => fmt2(v, 3) },
    { key: 'CURRENCY',                 label: 'Currency' },
    { key: 'RATE',                     label: 'Rate',            fmt: (v) => fmt2(v, 2) },
    { key: 'TOTALVALUE',               label: 'Total Value',     fmt: (v) => fmt2(v, 2) },
    { key: 'Contract_Date',            label: 'Contract Date',   fmt: fmtDate },
    { key: 'CERTIFICATE',              label: 'Certificate (WO)' },
    { key: 'CERTIFICATION_LEGAL',      label: 'Cert. Legal' },
    { key: 'CERTIFICATION_QUALITY',    label: 'Cert. Quality' },
  ];

  // ── Excel download ──────────────────────────────────────────────────────────
  const handleExcelDownload = async () => {
    if (!data) return;
    const { addTitleRow, addSubtitleRow, addHeaderRow, addDataRow, addTotalRow } = await import('../../utils/excelStyles');

    const wb = new ExcelJS.Workbook();
    wb.creator = 'Plywood Software Report';
    wb.created = new Date();
    const dateRange = `${fromDate.format('DD/MM/YYYY')} – ${toDate.format('DD/MM/YYYY')}`;
    const subtitle  = `Generated: ${new Date().toLocaleString()}   |   Date Range: ${dateRange}   |   Total: ${data.detail.length} records`;

    const numCols = ['PCS', 'CBM', 'RATE', 'TOTALVALUE', 'LENGTH', 'WIDTH', 'THICKNESS'];

    // ── Sheet 1: Detail ────────────────────────────────────────────────────
    const ws1 = wb.addWorksheet('Loading Sale Detail', { views: [{ state: 'frozen', ySplit: 3 }] });
    ws1.columns = detailHeaders.map(({ label }) => ({
      key: label, width: Math.max(label.length + 4, 14)
    }));

    addTitleRow(ws1, 'LOADING / SALE — DATE WISE', detailHeaders.length);
    addSubtitleRow(ws1, subtitle, detailHeaders.length);
    addHeaderRow(ws1, detailHeaders.map(h => h.label));

    data.detail.forEach((row, i) => {
      const values = detailHeaders.map(({ key, fmt }) =>
        fmt ? fmt(row[key]) : (row[key] ?? '')
      );
      const alignRight = detailHeaders
        .map(({ key }, idx) => numCols.includes(key) ? idx + 1 : null)
        .filter(Boolean);
      addDataRow(ws1, values, i, { boldCols: [2, 4], alignRight });
    });
    ws1.autoFilter = { from: { row: 3, column: 1 }, to: { row: 3, column: detailHeaders.length } };

    // ── Sheet 2: By Country ────────────────────────────────────────────────
    const ws2 = wb.addWorksheet('Summary by Country', { views: [{ state: 'frozen', ySplit: 3 }] });
    ws2.columns = [
      { key: 'country',  width: 22 },
      { key: 'currency', width: 12 },
      { key: 'cbm',      width: 14 },
      { key: 'rate',     width: 12 },
      { key: 'value',    width: 16 },
    ];
    addTitleRow(ws2, 'SUMMARY BY COUNTRY', 5);
    addSubtitleRow(ws2, subtitle, 5);
    addHeaderRow(ws2, ['Country', 'Currency', 'CBM', 'Avg Rate', 'Total Value']);

    data.byCountry.forEach((row, i) => {
      const isTotal = row.COUNTRY === 'GRAND TOTAL';
      const vals = [row.COUNTRY || '', row.CURRENCY || '', fmt2(row.CBM, 3), fmt2(row.RATE, 2), fmt2(row.TOTALVALUE, 2)];
      if (isTotal) {
        addTotalRow(ws2, vals);
      } else {
        addDataRow(ws2, vals, i, { boldCols: [1], alignRight: [3, 4, 5] });
      }
    });

    // ── Sheet 3: By Type / Certificate ────────────────────────────────────────
    const ws3 = wb.addWorksheet('By Type Certificate', { views: [{ state: 'frozen', ySplit: 3 }] });
    ws3.columns = [{ width: 20 }, { width: 20 }, { width: 12 }, { width: 14 }, { width: 12 }, { width: 16 }];
    addTitleRow(ws3, 'SUMMARY BY TYPE / CERTIFICATE', 6);
    addSubtitleRow(ws3, subtitle, 6);
    addHeaderRow(ws3, ['Type', 'Certificates', 'Currency', 'CBM', 'Avg Rate', 'Total Value']);
    data.byType.forEach((r, i) => {
      const isTotal = r.TYPE === 'GRAND TOTAL';
      const vals = [r.TYPE || '', r.CERTIFICATES || '', r.CURRENCY || '', fmt2(r.CBM, 3), fmt2(r.RATE, 2), fmt2(r.TOTALVALUE, 2)];
      if (isTotal) addTotalRow(ws3, vals);
      else addDataRow(ws3, vals, i, { boldCols: [1], alignRight: [4, 5, 6] });
    });

    // ── Sheet 4: By Type / Thickness ──────────────────────────────────────────
    const ws4 = wb.addWorksheet('By Type Thickness', { views: [{ state: 'frozen', ySplit: 3 }] });
    ws4.columns = [{ width: 20 }, { width: 20 }, { width: 12 }, { width: 12 }, { width: 14 }, { width: 12 }, { width: 16 }];
    addTitleRow(ws4, 'SUMMARY BY TYPE / THICKNESS', 7);
    addSubtitleRow(ws4, subtitle, 7);
    addHeaderRow(ws4, ['Type', 'Certificates', 'Thickness', 'Currency', 'CBM', 'Avg Rate', 'Total Value']);
    data.byTypeThick.forEach((r, i) => {
      const isTotal = r.TYPE === 'GRAND TOTAL';
      const vals = [r.TYPE || '', r.CERTIFICATES || '', r.THICKNESS || '', r.CURRENCY || '', fmt2(r.CBM, 3), fmt2(r.RATE, 2), fmt2(r.TOTALVALUE, 2)];
      if (isTotal) addTotalRow(ws4, vals);
      else addDataRow(ws4, vals, i, { boldCols: [1], alignRight: [5, 6, 7] });
    });

    const buf = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buf], { type: 'application/octet-stream' }),
      `Loading_Sale_${fromDate.format('YYYY-MM-DD')}_to_${toDate.format('YYYY-MM-DD')}.xlsx`
    );
  };

  // ── Search ──────────────────────────────────────────────────────────────────
  const handleSearch = async () => {
    setLoading(true);
    setError('');
    setData(null);
    try {
      const res = await fetch(`${API_URL}/container/loadingsale`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          from_date: fromDate.format('YYYY-MM-DD'),
          to_date:   toDate.format('YYYY-MM-DD'),
        }),
      });
      if (!res.ok) throw new Error('API error: ' + res.statusText);
      const apiData = await res.json();

      const detail      = apiData.data?.[0] || [];
      const byCountry   = apiData.data?.[1] || [];
      const byType      = apiData.data?.[2] || [];
      const byTypeThick = apiData.data?.[3] || [];

      if (detail.length === 0) {
        setError('No loading/sale data found for this date range.');
        return;
      }
      setData({ detail, byCountry, byType, byTypeThick });
      setPage(0);
    } catch (e) {
      setError(e.message || 'Fetch failed');
    } finally {
      setLoading(false);
    }
  };

  // ── Date picker shared props ────────────────────────────────────────────────
  const pickerSx = {
    flex: 1,
    '& .MuiOutlinedInput-root': {
      borderRadius: 2, backgroundColor: '#fff',
      '&:hover fieldset': { borderColor: '#1b4332' },
      '&.Mui-focused fieldset': { borderColor: '#1b4332', borderWidth: '2px' },
    },
  };
  const popperSx = {
    '& .MuiPaper-root': { borderRadius: 2, boxShadow: '0 8px 24px rgba(27,67,50,0.15)' },
    '& .MuiPickersDay-root': {
      borderRadius: 1.5,
      '&.Mui-selected': { backgroundColor: '#1b4332', '&:hover': { backgroundColor: '#0f2419' } },
    },
  };

  // ── Summary table renderer (Country) ───────────────────────────────────────
  const SummaryTable = ({ rows, nameKey, nameLabel }) => (
    <TableContainer component={Paper} sx={{ borderRadius: 0 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {[nameLabel, 'Currency', 'CBM', 'Avg Rate', 'Total Value'].map((h, i) => (
              <TableCell key={h} sx={{ ...hdrCell, textAlign: i === 0 ? 'left' : 'center' }}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => {
            const isTotal = row[nameKey] === 'GRAND TOTAL';
            return (
              <TableRow key={i} sx={{
                backgroundColor: isTotal ? '#1b4332' : (i % 2 === 0 ? '#fff' : '#f8faf9'),
                '&:hover': { backgroundColor: isTotal ? '#0f2419' : '#e8f5e9' },
              }}>
                <TableCell sx={dataCell({ fontWeight: isTotal ? 700 : 500, color: isTotal ? '#fff' : '#333' })}>
                  {row[nameKey] || '—'}
                </TableCell>
                <TableCell sx={dataCell({ textAlign: 'center', color: isTotal ? '#fff' : '#555' })}>
                  {row.CURRENCY || '—'}
                </TableCell>
                <TableCell sx={dataCell({ textAlign: 'right', fontWeight: isTotal ? 700 : 400, color: isTotal ? '#a7f3d0' : '#374151' })}>
                  {fmt2(row.CBM, 3)}
                </TableCell>
                <TableCell sx={dataCell({ textAlign: 'right', color: isTotal ? '#fff' : '#555' })}>
                  {fmt2(row.RATE, 2)}
                </TableCell>
                <TableCell sx={dataCell({ textAlign: 'right', fontWeight: isTotal ? 800 : 600, color: isTotal ? '#a7f3d0' : '#1b4332', fontSize: isTotal ? '0.9rem' : '0.82rem' })}>
                  {fmt2(row.TOTALVALUE, 2)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // ── Type/Certificate summary table ──────────────────────────────────────────
  const TypeTable = ({ rows, showThickness }) => (
    <TableContainer component={Paper} sx={{ borderRadius: 0 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {['Type', 'Certificates', ...(showThickness ? ['Thickness'] : []), 'Currency', 'CBM', 'Avg Rate', 'Total Value'].map((h, i) => (
              <TableCell key={h} sx={{ ...hdrCell, textAlign: i < (showThickness ? 3 : 2) ? 'left' : 'center' }}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => {
            const isTotal = row.TYPE === 'GRAND TOTAL';
            return (
              <TableRow key={i} sx={{
                backgroundColor: isTotal ? '#1b4332' : (i % 2 === 0 ? '#fff' : '#f8faf9'),
                '&:hover': { backgroundColor: isTotal ? '#0f2419' : '#e8f5e9' },
              }}>
                <TableCell sx={dataCell({ fontWeight: isTotal ? 700 : 500, color: isTotal ? '#fff' : '#333' })}>{row.TYPE || '—'}</TableCell>
                <TableCell sx={dataCell({ color: isTotal ? '#fff' : '#555' })}>{row.CERTIFICATES || '—'}</TableCell>
                {showThickness && <TableCell sx={dataCell({ textAlign: 'center', color: isTotal ? '#fff' : '#555' })}>{row.THICKNESS || '—'}</TableCell>}
                <TableCell sx={dataCell({ textAlign: 'center', color: isTotal ? '#fff' : '#555' })}>{row.CURRENCY || '—'}</TableCell>
                <TableCell sx={dataCell({ textAlign: 'right', fontWeight: isTotal ? 700 : 400, color: isTotal ? '#a7f3d0' : '#374151' })}>{fmt2(row.CBM, 3)}</TableCell>
                <TableCell sx={dataCell({ textAlign: 'right', color: isTotal ? '#fff' : '#555' })}>{fmt2(row.RATE, 2)}</TableCell>
                <TableCell sx={dataCell({ textAlign: 'right', fontWeight: isTotal ? 800 : 600, color: isTotal ? '#a7f3d0' : '#1b4332', fontSize: isTotal ? '0.9rem' : '0.82rem' })}>{fmt2(row.TOTALVALUE, 2)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Container maxWidth="xl" sx={{ mt: 2, pb: 4, px: { xs: 1, md: 2 } }}>

      {/* ── Search Card ── */}
      <Card sx={{
        mb: 4, borderRadius: 3,
        boxShadow: '0 4px 12px rgba(27,67,50,0.1)',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8faf9 100%)',
        border: '1px solid #e8f5e9'
      }}>
        <CardContent sx={{ p: { xs: 2, md: 4 } }}>
          <Box display="flex" alignItems="center" mb={3}>
            {onBackClick && (
              <IconButton onClick={onBackClick} sx={{ mr: 2, color: '#1b4332', '&:hover': { backgroundColor: '#f5f5f5' } }}>
                <ArrowBackIcon />
              </IconButton>
            )}
            <Box sx={{
              background: 'linear-gradient(135deg, #1b4332 0%, #2d6a4f 100%)',
              borderRadius: 2, p: 1.5, mr: 2,
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <ReceiptLongIcon sx={{ fontSize: 28, color: '#ffffff' }} />
            </Box>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#1b4332', letterSpacing: '-0.5px' }}>
                Loading / Sale - Date Wise
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                Search loading and sale data by date range
              </Typography>
            </Box>
          </Box>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2} alignItems="stretch">
              <DatePicker
                label="From Date"
                value={fromDate}
                onChange={setFromDate}
                slotProps={{ textField: { fullWidth: true, sx: pickerSx }, popper: { sx: popperSx } }}
              />
              <DatePicker
                label="To Date"
                value={toDate}
                onChange={setToDate}
                minDate={fromDate}
                slotProps={{ textField: { fullWidth: true, sx: pickerSx }, popper: { sx: popperSx } }}
              />
              <Button
                variant="contained"
                size="large"
                startIcon={<SearchIcon />}
                disabled={loading || !fromDate || !toDate}
                onClick={handleSearch}
                sx={{
                  minWidth: { xs: '100%', md: 160 },
                  borderRadius: 2, fontSize: '0.95rem', fontWeight: 600,
                  background: 'linear-gradient(135deg, #1b4332 0%, #2d6a4f 100%)',
                  color: 'white', textTransform: 'none',
                  boxShadow: '0 4px 12px rgba(27,67,50,0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #0f2419 0%, #1b4332 100%)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Search
              </Button>
            </Box>
          </LocalizationProvider>
        </CardContent>
      </Card>

      {/* ── Results header ── */}
      {data && (
        <Card sx={{
          mb: 3, borderRadius: 3,
          background: 'linear-gradient(135deg, #1b4332 0%, #2d6a4f 100%)',
          boxShadow: '0 4px 12px rgba(27,67,50,0.2)',
        }}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} gap={2}>
              <Box display="flex" alignItems="center" gap={2}>
                <Box sx={{ background: 'rgba(255,255,255,0.2)', borderRadius: 2, p: 1.5, display: 'flex', alignItems: 'center' }}>
                  <ReceiptLongIcon sx={{ fontSize: 32, color: '#ffffff' }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#ffffff' }}>
                    Loading / Sale Records Found
                  </Typography>
                  <Box display="flex" gap={1} alignItems="center" mt={0.5} flexWrap="wrap">
                    <Chip
                      label={`${data.detail.length} record${data.detail.length !== 1 ? 's' : ''}`}
                      size="small"
                      sx={{ backgroundColor: 'rgba(255,255,255,0.9)', color: '#1b4332', fontWeight: 600 }}
                    />
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                      {fromDate?.format('MMM DD, YYYY')} to {toDate?.format('MMM DD, YYYY')}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Button
                variant="contained"
                size="large"
                startIcon={<DownloadIcon />}
                onClick={handleExcelDownload}
                sx={{
                  minWidth: { xs: '100%', md: 180 },
                  borderRadius: 2, fontSize: '0.95rem', fontWeight: 600,
                  background: '#ffffff', color: '#1b4332', textTransform: 'none',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  '&:hover': { background: '#f1f8f4', transform: 'translateY(-2px)' },
                  transition: 'all 0.3s ease',
                }}
              >
                Download Excel
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* ── Loading / Error ── */}
      {loading && (
        <Box textAlign="center" py={8}>
          <CircularProgress size={50} sx={{ color: '#1b4332' }} />
          <Typography variant="body1" sx={{ mt: 2, color: '#666', fontWeight: 500 }}>
            Searching records...
          </Typography>
        </Box>
      )}
      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 1 }}>{error}</Alert>}

      {/* ── Tabs + Tables ── */}
      {data && (
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid #e8f5e9' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              sx={{
                '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.9rem', color: '#666' },
                '& .MuiTab-root.Mui-selected': { color: '#1b4332' },
                '& .MuiTabs-indicator': { backgroundColor: '#1b4332', height: 3 },
              }}
            >
              <Tab label={`Detail (${data.detail.length})`} />
              <Tab label={`By Country (${data.byCountry.length})`} />
              <Tab label={`By Type / Certificate (${data.byType.length})`} />
              <Tab label={`By Type / Thickness (${data.byTypeThick.length})`} />
            </Tabs>
          </Box>

          {/* Tab 0 — Detail (paginated) */}
          {activeTab === 0 && (() => {
            const totalPages = Math.ceil(data.detail.length / PAGE_SIZE);
            const pageRows   = data.detail.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
            return (
              <>
                <Typography variant="caption" sx={{ display: { xs: 'block', md: 'none' }, textAlign: 'center', color: '#666', py: 1, fontStyle: 'italic' }}>
                  ← Swipe left to see all columns →
                </Typography>
                <Box sx={{ overflowX: 'auto' }}>
                  <TableContainer>
                    <Table sx={{ minWidth: 2000 }} size="small">
                      <TableHead>
                        <TableRow>
                          {detailHeaders.map(({ label }) => (
                            <TableCell key={label} sx={hdrCell}>{label}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pageRows.map((row, i) => (
                          <TableRow key={page * PAGE_SIZE + i} sx={{
                            backgroundColor: i % 2 === 0 ? '#ffffff' : '#f8faf9',
                            '&:hover': { backgroundColor: '#e8f5e9' },
                          }}>
                            {detailHeaders.map(({ key, fmt }) => (
                              <TableCell key={key} sx={dataCell({
                                fontWeight: key === 'CONTAINER_NO' || key === 'ORDERNO' || key === 'INVOICE_NUMBER' ? 600 : 400,
                                color: key === 'CONTAINER_NO' || key === 'ORDERNO' ? '#1b4332' : key === 'INVOICE_NUMBER' ? '#1565c0' : '#444',
                                textAlign: ['PCS','CBM','RATE','TOTALVALUE','LENGTH','WIDTH','THICKNESS'].includes(key) ? 'right' : 'left',
                              })}>
                                {fmt ? fmt(row[key]) : (row[key] ?? '—')}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                {/* Pagination controls */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, borderTop: '1px solid #e5e7eb', flexWrap: 'wrap', gap: 1 }}>
                  <Typography sx={{ fontSize: '0.82rem', color: '#6b7280' }}>
                    Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, data.detail.length)} of {data.detail.length} records
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <button
                      onClick={() => setPage(0)}
                      disabled={page === 0}
                      style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid #d1d5db', cursor: page === 0 ? 'not-allowed' : 'pointer', backgroundColor: page === 0 ? '#f9fafb' : '#fff', color: '#374151', fontSize: '0.8rem' }}
                    >
                      «
                    </button>
                    <button
                      onClick={() => setPage(p => Math.max(0, p - 1))}
                      disabled={page === 0}
                      style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid #d1d5db', cursor: page === 0 ? 'not-allowed' : 'pointer', backgroundColor: page === 0 ? '#f9fafb' : '#fff', color: '#374151', fontSize: '0.8rem' }}
                    >
                      ‹ Prev
                    </button>
                    <Box sx={{ px: 1.5, py: 0.5, borderRadius: 1, backgroundColor: '#1b4332', color: '#fff', fontSize: '0.8rem', fontWeight: 600 }}>
                      {page + 1} / {totalPages}
                    </Box>
                    <button
                      onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                      disabled={page >= totalPages - 1}
                      style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid #d1d5db', cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer', backgroundColor: page >= totalPages - 1 ? '#f9fafb' : '#fff', color: '#374151', fontSize: '0.8rem' }}
                    >
                      Next ›
                    </button>
                    <button
                      onClick={() => setPage(totalPages - 1)}
                      disabled={page >= totalPages - 1}
                      style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid #d1d5db', cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer', backgroundColor: page >= totalPages - 1 ? '#f9fafb' : '#fff', color: '#374151', fontSize: '0.8rem' }}
                    >
                      »
                    </button>
                  </Box>
                </Box>
              </>
            );
          })()}

          {/* Tab 1 — By Country */}
          {activeTab === 1 && (
            <SummaryTable rows={data.byCountry} nameKey="COUNTRY" nameLabel="Country" />
          )}

          {/* Tab 2 — By Type / Certificate */}
          {activeTab === 2 && (
            <TypeTable rows={data.byType} showThickness={false} />
          )}

          {/* Tab 3 — By Type / Thickness */}
          {activeTab === 3 && (
            <TypeTable rows={data.byTypeThick} showThickness={true} />
          )}
        </Card>
      )}

      {/* ── Empty state ── */}
      {!data && !loading && !error && (
        <Card sx={{
          p: 8, textAlign: 'center', borderRadius: 3,
          background: 'linear-gradient(135deg, #f8faf9 0%, #ffffff 100%)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          border: '2px dashed #d0e8d5'
        }}>
          <Box sx={{
            background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
            borderRadius: '50%', width: 100, height: 100,
            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto', mb: 3
          }}>
            <CalendarTodayIcon sx={{ fontSize: 50, color: '#1b4332' }} />
          </Box>
          <Typography variant="h5" sx={{ color: '#1b4332', fontWeight: 700, mb: 1 }}>
            No Data to Display
          </Typography>
          <Typography variant="body1" sx={{ color: '#666', mt: 1 }}>
            Select a date range above and click Search to view loading / sale data
          </Typography>
        </Card>
      )}
    </Container>
  );
};

export default LoadingSaleView;
